import { Download } from "lucide-react";
import { downloadCsv, tableToGrid } from "./csv";

export type Cell = { t: string; rs?: number; cs?: number; a?: boolean; g?: boolean; b?: boolean; parts?: Part[] };
export type Table = { head: string[]; rows: Cell[][]; caption?: string };
export type Part = { p?: string; b?: boolean; table?: Table };

const NUM = /^(~|\$|<)?[\d.,]+(%|M|°C\+?| MT| MT CO₂e| MWh| m³)?$|^n\/a$|^–$/;

export function Assured() {
  return (
    <svg viewBox="0 0 12 12" className="ml-1 inline-block h-[11px] w-[11px] align-[-1px] text-[var(--am-gray)]" role="img" aria-label="Assured by ERM CVS">
      <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1" />
      <circle cx="6" cy="6" r="2" fill="currentColor" />
    </svg>
  );
}

export function RichText({ text, className = "" }: { text: string; className?: string }) {
  const blocks = text.split("\n\n");
  return (
    <div className={`space-y-3 ${className}`}>
      {blocks.map((b, i) => {
        const lines = b.split("\n");
        const bullets = lines.filter((l) => l.startsWith("• "));
        const lead = lines.filter((l) => !l.startsWith("• "));
        return (
          <div key={i}>
            {lead.length > 0 && <p>{lead.join(" ")}</p>}
            {bullets.length > 0 && (
              <ul className="mt-1.5 list-disc space-y-1 pl-5">{bullets.map((l) => <li key={l}>{l.slice(2)}</li>)}</ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Visual column index of every cell, accounting for row and column spans
function layout(rows: Cell[][]) {
  const taken: Record<string, boolean> = {};
  return rows.map((row, r) => {
    let c = 0;
    return row.map((cell) => {
      while (taken[`${r}:${c}`]) c++;
      const at = c;
      for (let i = 0; i < (cell.rs ?? 1); i++) for (let j = 0; j < (cell.cs ?? 1); j++) taken[`${r + i}:${at + j}`] = true;
      c += cell.cs ?? 1;
      return at;
    });
  });
}

export function CellBody({ cell }: { cell: Cell }) {
  if (cell.parts) {
    return (
      <div className="space-y-4">
        {cell.parts.map((p, i) => p.table
          ? <DataTable key={i} table={p.table} nested />
          : <RichText key={i} text={p.p ?? ""} className={p.b ? "font-semibold text-[#2b2d30]" : ""} />)}
      </div>
    );
  }
  return <>{cell.t.includes("\n") ? <RichText text={cell.t} /> : cell.t}{cell.a && <Assured />}</>;
}

export function CsvButton({ table, name, testId }: { table: Table; name: string; testId: string }) {
  return (
    <button type="button" onClick={() => downloadCsv(`applied-materials-2025-${name}.csv`, tableToGrid(table))} data-testid={testId}
      className="am-chip gap-1.5 py-1" aria-label={`Download CSV: ${name.replace(/-/g, " ")}`}>
      <Download size={13} aria-hidden /> Download CSV
    </button>
  );
}

export function DataTable({ table, nested = false, testId }: { table: Table; nested?: boolean; testId?: string }) {
  const hl = table.head.indexOf("FY 2025");
  const cols = layout(table.rows);
  const headCls = nested ? "bg-[var(--am-blue)] text-white" : "bg-[var(--am-yellow)] text-[#2b2d30]";
  return (
    <div className="am-scroll-x overflow-x-auto" data-testid={testId}>
      {table.caption && <p className="mb-2 text-sm">{table.caption}</p>}
      <table className={`w-full border-collapse text-left text-[13px] leading-snug sm:text-sm ${nested ? "min-w-[560px]" : table.head.length <= 3 ? "" : "min-w-[640px]"}`}>
        <thead>
          <tr className={headCls}>
            {table.head.map((h, i) => (
              <th key={i} scope="col" className={`px-3 py-2.5 font-semibold ${h.startsWith("FY") ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r} className="border-b border-[#c9cbcd] align-top">
              {row.map((cell, i) => {
                if (cell.g) {
                  return <th key={i} colSpan={cell.cs} scope="colgroup" className="border-b-2 border-[var(--am-blue)] px-3 pb-1.5 pt-4 font-semibold text-[var(--am-blue)]">{cell.t}{cell.a && <Assured />}</th>;
                }
                const col = cols[r][i];
                const right = NUM.test(cell.t.trim());
                const isLabel = col === 0 && !right;
                const Tag = isLabel ? "th" : "td";
                return (
                  <Tag key={i} rowSpan={cell.rs} colSpan={cell.cs} scope={isLabel ? "row" : undefined}
                    className={`px-3 py-2 ${isLabel ? "font-normal" : ""} ${cell.b ? "font-semibold" : ""} ${right ? "whitespace-nowrap text-right tabular-nums" : ""} ${col === hl && !nested && !cell.cs ? "bg-[#dbf1f9] font-semibold text-[#2b2d30]" : ""}`}>
                    <CellBody cell={cell} />
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
